import React, { useEffect, useState } from "react";
import {
  ProgressContainer,
  ProgressFill,
  Divider,
  LabelContainer,
  Label,
  Container,
} from "./styles";

type KLProgressBarProps = {
  currentStep: number;
  totalSteps: number;
  labels: string[];
};

const KLProgressBar: React.FC<KLProgressBarProps> = ({
  currentStep,
  totalSteps,
  labels,
}) => {
  // Validate inputs to prevent array bounds errors
  if (currentStep < 1 || currentStep > totalSteps) {
    console.error(`Invalid currentStep: ${currentStep} (totalSteps: ${totalSteps})`);
  }

  if (labels.length !== totalSteps) {
    console.error(`Labels array length (${labels.length}) doesn't match totalSteps (${totalSteps})`);
  }

  // Safely clamp currentStep to valid range
  const safeCurrentStep = Math.max(1, Math.min(currentStep, totalSteps));
  const targetProgress = (safeCurrentStep / totalSteps) * 100;
  const previousProgress = ((safeCurrentStep - 1) / totalSteps) * 100;
  const [animatedProgress, setAnimatedProgress] = useState(previousProgress);
  const sectionWidth = 100 / totalSteps;

  // Get current label safely with fallback
  const currentLabel = labels[safeCurrentStep - 1] || '';

  // Animate progress from previous step to current step
  useEffect(() => {
    // Start from the previous step's progress
    setAnimatedProgress(previousProgress);

    // Small delay before starting animation to current step
    const timer = setTimeout(() => {
      setAnimatedProgress(targetProgress);
    }, 150);

    return () => clearTimeout(timer);
  }, [targetProgress, previousProgress]);

  return (
    <Container
      role="progressbar"
      aria-valuenow={safeCurrentStep}
      aria-valuemin={1}
      aria-valuemax={totalSteps}
      aria-valuetext={`שלב ${safeCurrentStep} מתוך ${totalSteps}: ${currentLabel}`}
    >
      <LabelContainer role="list" aria-label="שלבי התהליך">
        {labels.map((label, index) => {
          const stepNumber = index + 1;
          const isActive = stepNumber === safeCurrentStep;
          const isCompleted = stepNumber < safeCurrentStep;
          const labelText = label || 'לא זמין';

          return (
            <Label
              key={index}
              $isActive={isActive}
              $isCompleted={isCompleted}
              role="listitem"
              aria-current={isActive ? "step" : undefined}
              aria-label={`שלב ${stepNumber}: ${labelText}${isCompleted ? ' - הושלם' : ''}`}
            >
              {labelText}
            </Label>
          );
        })}
      </LabelContainer>
      <ProgressContainer>
        <ProgressFill $progress={animatedProgress} />
        {Array.from({ length: totalSteps - 1 }).map((_, index) => (
          <Divider key={index} $position={(index + 1) * sectionWidth} />
        ))}
      </ProgressContainer>
    </Container>
  );
};

export default KLProgressBar;
